import { type SettingsNavGates } from '@apps/web/components/settings/settings_nav_items';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { isPlanOfCareDutyMonitoringEnabled } from '@apps/web/lib/notification_settings_catalog';
import { canManageOrgSettings } from '@apps/web/lib/organization_permissions';
import {
    useOrganization,
    useUserProfile,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

/**
 * Derives the feature/permission gates used by the settings sidebar nav.
 * Replicates the same derivations as the monolith settings.tsx so the swap
 * task can wire this in place of inline derivations.
 */
export function useSettingsGates(): {
    readonly organizationId: string | null;
    readonly isLoading: boolean;
    readonly planOfCareDutyMonitoringAvailable: boolean;
    readonly planOfCareDutyMonitoringEnabled: boolean;
    readonly clockWriteEnabled: boolean;
    readonly gates: SettingsNavGates;
} {
    const organizationId = useOrganizationId();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'settings',
    });
    const { data: userProfile } = useUserProfile({ reason: 'settings' });

    const clockInOut = useFeatureFlag(
        'clock_in_out_processing_enabled',
        organization?.features,
    );
    const slack = useFeatureFlag('slack_oauth_enabled', organization?.features);
    const caregiverEngagement = useFeatureFlag(
        'caregiver_engagement_texts_enabled',
        organization?.features,
    );
    const agentV3 = useFeatureFlag('agent_v3_enabled', organization?.features);
    // Phoebe staff turn these on per organization; without either, no clock
    // write can reach the EHR, so clock-write settings have nothing to control.
    const clockInWrite = useFeatureFlag(
        'allow_clock_in_write',
        organization?.features,
    );
    const clockOutWrite = useFeatureFlag(
        'allow_clock_out_write',
        organization?.features,
    );
    const coordinatorAutomationsFlag = useFeatureFlag(
        'coordinator_automations_enabled',
        organization?.features,
    );
    const phoneSetupComplete = useFeatureFlag(
        'voip_integration_setup_complete',
        organization?.features,
    );
    // The router only awaits `/users/me`, not the profile/org reads these gates
    // derive from. Until those resolve, `canManageOrgSettings` is transiently
    // false; callers use this flag to wait instead of acting on it (which would
    // bounce admins off deep-linked org pages on cold load).
    //
    // A null `organizationId` while the profile has memberships is also
    // transient: the organization provider resolves the stored selection in
    // an effect after the profile loads, and the selection always lands on a
    // membership. Deciding org-gated behavior (e.g. a feature-flag gate) in
    // that frame bounces cold deep links off their path.
    const isLoading =
        userProfile === undefined ||
        (organizationId !== null && organization === undefined) ||
        (organizationId === null && userProfile.org_member_profiles.length > 0);
    const planOfCareDutyMonitoringAvailable =
        organization?.general_settings
            ?.plan_of_care_duty_monitoring_available === true;

    return {
        organizationId,
        isLoading,
        planOfCareDutyMonitoringAvailable,
        planOfCareDutyMonitoringEnabled: isPlanOfCareDutyMonitoringEnabled(
            organization?.general_settings,
        ),
        clockWriteEnabled: clockInWrite || clockOutWrite,
        gates: {
            // Phoebe staff. `user.admin` is the internal-staff column, not an
            // agency-admin role, so this stays false for every customer.
            internalAdmin: userProfile?.user.admin === true,
            agentV3,
            coordinatorAutomations: agentV3 && coordinatorAutomationsFlag,
            canManageOrgSettings: canManageOrgSettings(
                userProfile,
                organizationId,
            ),
            // Mirrors userCanAccessDataSync in settings.tsx.
            canAccessDataSync: Boolean(organizationId),
            caregiverEngagement,
            careCoordinator:
                organization?.features?.care_coordinator_support_enabled ??
                false,
            clockInOut,
            dailyDigest: organization?.daily_digest_settings?.enabled === true,
            phoneSetup: !phoneSetupComplete,
            slack,
            // OrganizationRead has no static offices_count field; the monolith
            // always renders OfficeAssignmentCard when organizationId is set
            // and the card self-hides when offices are empty. Fall back to
            // Boolean(organizationId) as the closest equivalent signal.
            multiOffice: Boolean(organizationId),
        },
    };
}
